import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CircularProgress, InputAdornment, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { PLATFORM_DOMAIN_SUFFIX, SLUG_MAX_LENGTH, isValidSlug, platformHostname, slugify } from "../utils/domain";

const DEBOUNCE_MS = 400;

// Slug input with a debounced availability check. Reports
// `{slug, state: "idle"|"checking"|"available"|"taken"|"invalid"|"unknown", hostname}`
// through onStatus so the parent can gate its Save/Next button.
function SlugField({ value, onChange, onStatus, checkSlug, currentSlug, disabled, label }) {
  const [status, setStatus] = useState({ state: "idle", hostname: "", suggestion: "" });

  useEffect(() => {
    const slug = value;
    if (!slug) {
      setStatus({ state: "idle", hostname: "", suggestion: "" });
      return undefined;
    }
    if (!isValidSlug(slug)) {
      setStatus({ state: "invalid", hostname: platformHostname(slug), suggestion: "" });
      return undefined;
    }
    if (currentSlug && slug === currentSlug) {
      setStatus({ state: "available", hostname: platformHostname(slug), suggestion: "" });
      return undefined;
    }
    let cancelled = false;
    setStatus({ state: "checking", hostname: platformHostname(slug), suggestion: "" });
    const timer = window.setTimeout(async () => {
      try {
        const result = await checkSlug(slug);
        if (cancelled) return;
        setStatus({
          state: result?.available ? "available" : "taken",
          hostname: result?.hostname || platformHostname(slug),
          suggestion: result?.suggestion || "",
        });
      } catch {
        if (!cancelled) setStatus({ state: "unknown", hostname: platformHostname(slug), suggestion: "" });
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [value, currentSlug, checkSlug]);

  useEffect(() => {
    if (onStatus) onStatus({ slug: value, ...status });
  }, [onStatus, status, value]);

  const helper = {
    idle: "Lowercase letters, numbers and hyphens.",
    checking: "Checking availability…",
    available: `Your website will be at ${status.hostname}`,
    taken: "That address is already taken.",
    invalid: "Use 3–40 lowercase letters, numbers or hyphens, starting and ending with a letter or number.",
    unknown: `Couldn't check availability right now. It is checked again when you save. (${status.hostname})`,
  }[status.state];

  return (
    <MDBox>
      <TextField
        fullWidth
        disabled={disabled}
        label={label}
        value={value}
        onChange={(event) => onChange(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, SLUG_MAX_LENGTH))}
        onBlur={() => onChange(slugify(value))}
        error={status.state === "taken" || status.state === "invalid"}
        helperText={helper}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {status.state === "checking" ? <CircularProgress size={16} /> : <MDTypography variant="caption" color="text">.{PLATFORM_DOMAIN_SUFFIX}</MDTypography>}
            </InputAdornment>
          ),
        }}
      />
      {status.state === "taken" && status.suggestion && (
        <MDButton size="small" variant="text" color="info" sx={{ px: 0 }} onClick={() => onChange(status.suggestion)}>
          Use {status.suggestion} instead
        </MDButton>
      )}
    </MDBox>
  );
}

SlugField.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onStatus: PropTypes.func,
  checkSlug: PropTypes.func.isRequired,
  currentSlug: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
};

SlugField.defaultProps = { onStatus: null, currentSlug: "", disabled: false, label: "Website address" };

export default SlugField;
